/**
 * Retirement village — state/territory consumer-protection framework.
 * Ported verbatim from frontend/src/constants/retirementVillageStateRules.js. This data isn't used
 * by the FINANCIAL modelling (that's federal, lives in the engine) — it's consumed by
 * RetirementVillageCalculations-adjacent code purely to attach the right jurisdiction's
 * buyback/recurrent-charge disclosure text to a projection result.
 */

export interface RetirementVillageStateRule {
    code: string;
    name: string;
    act: string;
    regulator: string;
    regulatorUrl: string;
    disclosureDocument: string;
    buyback: { hasStatutoryTimeframe: boolean; summary: string };
    recurrentChargesAfterVacating: string;
    note: string;
}

/** Ordered list for state pickers. */
export const RETIREMENT_VILLAGE_STATES = [
    { code: 'NSW', name: 'New South Wales' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'SA', name: 'South Australia' },
    { code: 'WA', name: 'Western Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'ACT', name: 'Australian Capital Territory' },
    { code: 'NT', name: 'Northern Territory' },
];

export const RETIREMENT_VILLAGE_STATE_RULES: Record<string, RetirementVillageStateRule> = {
    NSW: {
        code: 'NSW',
        name: 'New South Wales',
        act: 'Retirement Villages Act 1999 (NSW)',
        regulator: 'NSW Fair Trading',
        regulatorUrl: 'https://www.fairtrading.nsw.gov.au/housing-and-property/retirement-villages',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: true,
            summary:
                'If your home has not re-sold, registered interest holders (a registered long-term lease with at least 50% of any capital gain) can apply for their exit entitlement after 6 months (Sydney metropolitan, the Blue Mountains, Wollongong and Newcastle) or 12 months (rest of NSW). The clock generally starts about 40 days after the home is listed or you vacate. Strata, community and company-title residents are not covered by this rule.',
        },
        recurrentChargesAfterVacating:
            'Recurrent charges for general services are capped at 42 days after you permanently vacate (for registered interest holders).',
        note: 'Cooling-off period of 7 days after signing. Other exit costs (departure/deferred management fee, reinstatement, capital-loss share) are set by your contract.',
    },
    VIC: {
        code: 'VIC',
        name: 'Victoria',
        act: 'Retirement Villages Act 1986 (Vic)',
        regulator: 'Consumer Affairs Victoria',
        regulatorUrl: 'https://www.consumer.vic.gov.au/housing/retirement-villages',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: true,
            summary:
                'Under Victoria’s reforms, residents on a lease or licence are generally entitled to be paid their exit entitlement within about 6 months of permanently leaving, even if the home has not re-sold.',
        },
        recurrentChargesAfterVacating:
            'Your liability for ongoing charges after permanently vacating is limited under the Act — check your contract for the exact period.',
        note: '3 clear business days cooling-off. Departure fee, reinstatement and capital-gain/loss sharing are set by your contract.',
    },
    QLD: {
        code: 'QLD',
        name: 'Queensland',
        act: 'Retirement Villages Act 1999 (Qld)',
        regulator: 'Queensland Government (Fair Trading)',
        regulatorUrl: 'https://www.qld.gov.au/housing/buying-owning-home/advice-buying-home/retirement-villages',
        disclosureDocument: 'Village Comparison Document & Prospective Costs Document',
        buyback: {
            hasStatutoryTimeframe: true,
            summary:
                'If your home has not re-sold, the operator must pay your exit entitlement after 18 months from when you leave or give notice. You can choose your own real-estate agent if it is unsold after 6 months.',
        },
        recurrentChargesAfterVacating:
            'You stop paying recurrent (general services) charges once the resale is settled or the statutory period ends — check your contract.',
        note: 'Strong statutory protections. Note the 18-month clock typically starts only after you vacate, complete reinstatement and appoint an agent. Cooling-off period applies.',
    },
    SA: {
        code: 'SA',
        name: 'South Australia',
        act: 'Retirement Villages Act 2016 (SA)',
        regulator: 'SA Consumer and Business Services',
        regulatorUrl: 'https://www.cbs.sa.gov.au/retirement-villages',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: true,
            summary:
                'If your home has not re-sold, the operator must repay your exit entitlement 18 months after you move out or give notice.',
        },
        recurrentChargesAfterVacating:
            'Liability for ongoing charges after vacating is limited under the Act — check your contract for the exact period.',
        note: 'Cooling-off period applies. Departure fee, reinstatement and capital-gain/loss sharing are set by your contract.',
    },
    WA: {
        code: 'WA',
        name: 'Western Australia',
        act: 'Retirement Villages Act 1992 (WA), as amended 2024',
        regulator: 'Consumer Protection (WA)',
        regulatorUrl: 'https://www.consumerprotection.wa.gov.au/retirement-villages',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: true,
            summary:
                'Under the 2024 reforms, exit entitlements for a lease-for-life (or buyback of a strata-titled home) must generally be paid within 12 months of you permanently leaving. Operators can apply to the Commissioner for an extension in limited cases.',
        },
        recurrentChargesAfterVacating:
            'The Act limits ongoing charges after permanently vacating and lets you ask the operator for help with aged-care fees while you wait — check your contract.',
        note: 'Reform commencement is staged from 2024–26. No transfer duty on a compulsory buyback. Reinstatement/refurbishment rules clarified.',
    },
    TAS: {
        code: 'TAS',
        name: 'Tasmania',
        act: 'Retirement Villages Act 2004 (Tas)',
        regulator: 'Consumer, Building and Occupational Services (CBOS)',
        regulatorUrl: 'https://www.cbos.tas.gov.au/topics/housing/retirement-villages',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: false,
            summary:
                'Tasmania does not set a mandatory statutory buyback timeframe — when you (or your estate) are repaid depends on when the home re-sells and on your contract. Many operators offer a voluntary buyback guarantee; check your contract.',
        },
        recurrentChargesAfterVacating:
            'How long you keep paying ongoing charges after vacating is governed by your contract — check it carefully.',
        note: 'Disputes can go to the Director of Consumer Affairs and Fair Trading and then TASCAT. Confirm exit terms in your contract.',
    },
    ACT: {
        code: 'ACT',
        name: 'Australian Capital Territory',
        act: 'Retirement Villages Act 2012 (ACT)',
        regulator: 'Access Canberra',
        regulatorUrl: 'https://www.accesscanberra.act.gov.au/',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: false,
            summary:
                'The ACT does not mandate a fixed statutory buyback timeframe — repayment timing depends on resale and your contract. Some operators offer a voluntary buyback guarantee; check your contract.',
        },
        recurrentChargesAfterVacating:
            'How long you keep paying ongoing charges after vacating is governed by your contract — check it carefully.',
        note: 'Confirm exit-entitlement and recurrent-charge terms in your contract and with Access Canberra.',
    },
    NT: {
        code: 'NT',
        name: 'Northern Territory',
        act: 'Retirement Villages Act 1995 (NT)',
        regulator: 'NT Consumer Affairs',
        regulatorUrl: 'https://consumeraffairs.nt.gov.au/',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: false,
            summary:
                'The NT does not mandate a fixed statutory buyback timeframe — repayment timing depends on resale and your contract. Some operators offer a voluntary buyback guarantee; check your contract.',
        },
        recurrentChargesAfterVacating:
            'How long you keep paying ongoing charges after vacating is governed by your contract — check it carefully.',
        note: 'Confirm exit-entitlement and recurrent-charge terms in your contract and with NT Consumer Affairs.',
    },
};

/**
 * Look up the consumer-protection rules for a state/territory code (e.g. 'NSW'). Falls back to a
 * national, state-neutral summary when the state is unknown.
 */
export function getRetirementVillageStateRules(code: string | null | undefined): RetirementVillageStateRule {
    const key = (code || '').toString().toUpperCase();
    if (RETIREMENT_VILLAGE_STATE_RULES[key]) return RETIREMENT_VILLAGE_STATE_RULES[key]!;
    return {
        code: '',
        name: 'your state or territory',
        act: 'your state or territory’s Retirement Villages Act',
        regulator: 'your state or territory consumer-protection regulator',
        regulatorUrl: 'https://www.australia.gov.au/',
        disclosureDocument: 'Disclosure Statement',
        buyback: {
            hasStatutoryTimeframe: false,
            summary:
                'Exit-entitlement (buyback) rules differ by state and territory. When you (or your estate) are repaid depends on your contract and your state’s Retirement Villages Act.',
        },
        recurrentChargesAfterVacating:
            'How long you keep paying ongoing charges after vacating depends on your state and your contract.',
        note: 'Select your state to see the rules that apply to you, and always check your village’s disclosure statement and contract.',
    };
}
